/**
 * Custom hook for managing PDF presentation mode
 *
 * Extracted from PDFViewerModal.tsx
 */

import { useState, useEffect, useCallback } from 'react';

export interface UsePDFPresentationProps {
  containerRef: React.RefObject<HTMLDivElement>;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
}

export interface UsePDFPresentationReturn {
  presentationMode: boolean;
  enterPresentationMode: () => Promise<void>;
  exitPresentationMode: () => Promise<void>;
}

/**
 * Manages PDF presentation mode (fullscreen)
 */
export function usePDFPresentation({
  containerRef,
  goToPreviousPage,
  goToNextPage,
}: UsePDFPresentationProps): UsePDFPresentationReturn {
  const [presentationMode, setPresentationMode] = useState(false);

  // Handle fullscreen change
  useEffect(() => {
    if (!presentationMode) return;

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setPresentationMode(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [presentationMode]);

  const enterPresentationMode = useCallback(async () => {
    try {
      if (containerRef.current) {
        await containerRef.current.requestFullscreen();
        setPresentationMode(true);
      }
    } catch (error) {
      console.error('Error entering presentation mode:', error);
    }
  }, [containerRef]);

  const exitPresentationMode = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      setPresentationMode(false);
    } catch (error) {
      console.error('Error exiting presentation mode:', error);
    }
  }, []);

  return {
    presentationMode,
    enterPresentationMode,
    exitPresentationMode,
  };
}
